import { gsap } from 'gsap';
import { onMounted, ref, toValue, type MaybeRef, type MaybeRefOrGetter } from 'vue';

export type ElementToAnimate = {
  el: MaybeRefOrGetter<HTMLElement | null>;
  /**
   * Delay before the animation starts (in seconds).
   * @default 0
   */
  delay?: MaybeRefOrGetter<number>;
  /**
   * Duration of the animation (in seconds).
   * @default 1
   */
  duration?: number;
  /**
   * The animation to use.
   * @default 'slide'
   */
  animation?: 'slide' | 'fade' | 'scale';
} & (ElementToAnimateSlide | ELementToAnimateFade | ElementToAnimateScale);

interface ElementToAnimateSlide {
  animation?: 'slide';
  animationOptions?: {
    direction?: 'x' | 'y';
    offsetMultiplier?: number;
  };
}

interface ELementToAnimateFade {
  animation?: 'fade';
}
interface ElementToAnimateScale {
  animation?: 'scale';
  animationOptions: {
    wrapperEl: MaybeRefOrGetter<HTMLElement | null>;
  };
}

export function useAnimation({
  parentEl,
  elements,
  threshold,
  stagger = 0,
}: {
  parentEl: MaybeRef<HTMLElement | null>;
  elements: MaybeRefOrGetter<ElementToAnimate[]>;
  /**
   * The threshold at which the animation should trigger.
   * This is a value between 0 and 1, where 0 means the animation will trigger as soon as any part of the element is in view,
   * and 1 means the animation will only trigger when the entire element is in view.
   * @default 0.33
   */
  threshold?: number;
  /**
   * Allows automatic staggering of the animation.
   * Setting it to 0 will disable it.
   * @default 0
   */
  stagger?: number;
}) {
  const isVisible = ref(false);

  const intersectionObserver = ref<IntersectionObserver | null>(null);

  onMounted(() => {
    const observedEl = toValue(parentEl);
    if (!observedEl) {
      return;
    }

    // If the element is larger than the viewport, we set the threshold to 0.
    const elHeight = toValue(parentEl)?.offsetHeight ?? 0;
    const windowHeight = typeof window === 'undefined' ? 0 : window.innerHeight;
    const actualThreshold = threshold ?? (elHeight * 3 > windowHeight ? 0 : 0.33);

    /**
     * Setup the intersection observer to trigger when the component is in view.
     */
    const observer = new IntersectionObserver(
      entries => {
        const latestEntry = entries.sort((a, b) => b.time - a.time)[0];
        if (latestEntry?.isIntersecting) {
          show();
          observer.disconnect();
        }
      },
      {
        threshold: actualThreshold,
      }
    );
    observer.observe(toValue(parentEl)!);

    intersectionObserver.value = observer;
  });

  function show() {
    const elementsToAnimate = toValue(elements).filter(entry => toValue(entry.el) !== null);

    let staggerDelay = 0;

    elementsToAnimate.forEach(entry => {
      const sharedVars: gsap.TweenVars = {
        delay: staggerDelay + (toValue(entry.delay) ?? 0),
        duration: entry.duration ?? 1,
        autoAlpha: 1,
        ease: 'power2.out',
      };

      const el = toValue(entry.el);

      entry.animation = entry.animation ?? 'slide';

      switch (entry.animation) {
        case 'slide': {
          const animationOptions = entry.animationOptions ?? {};
          const baseOffset = 20;
          const offsetMultiplier = animationOptions?.offsetMultiplier ?? 1;
          const direction = animationOptions?.direction ?? 'y';
          if (el) {
            gsap.set(el, {
              x: direction === 'x' ? baseOffset * offsetMultiplier : undefined,
              y: direction === 'y' ? baseOffset * offsetMultiplier : undefined,
              overwrite: true,
            });
            gsap.to(el, { ...sharedVars, x: 0, y: 0 });
          }
          break;
        }
        case 'fade': {
          gsap.to(el, { ...sharedVars });
          break;
        }
        case 'scale': {
          const wrapperEl = toValue(entry.animationOptions.wrapperEl);
          if (!wrapperEl) {
            console.error('No wrapper element provided for scale animation');
            return;
          }
          wrapperEl.style.overflow = 'hidden'; // Needed to prevent the scale animation from overflowing
          gsap.set(wrapperEl, { scale: 0.9 });
          gsap.set(el, { scale: 1.25 });

          gsap.to(wrapperEl, { ...sharedVars, scale: 1 });
          gsap.to(el, { ...sharedVars, scale: 1 });
          break;
        }
      }

      if (stagger > 0) {
        staggerDelay += stagger;
      }
    });

    isVisible.value = true;
  }

  function hide(options: { vars: gsap.TweenVars } = { vars: {} }) {
    const elementsToAnimate = toValue(elements)
      .map(entry => toValue(entry.el))
      .filter(el => el !== null);
    gsap.to(elementsToAnimate, { autoAlpha: 0, duration: 0.5, ...options.vars });

    isVisible.value = false;
  }

  return {
    show,
    hide,
    isVisible,
  };
}
